import { z } from 'zod';
/**
 * The body of a `400 VALIDATION_FAILED`, and the only shape `details` ever holds on
 * the wire.
 *
 * ONE SHAPE FOR TWO KINDS OF REFUSAL, which is the point. `validationError()`
 * (`apps/api/src/lib/errors.ts`) builds it from `ZodError.flatten()` when a payload's
 * SHAPE is wrong, and `flowValidationError()`
 * (`apps/api/src/modules/apps/visitor/flow-validation.ts`) builds the same shape from
 * the rules that need stored rows — so a console needs one renderer for "this save was
 * refused", not two.
 *
 *  - `formErrors` — refusals with no field to attach to (`EMPTY_UPDATE`, a body that is
 *    not an object at all). Render them at the top of the form.
 *  - `fieldErrors` — keyed by the control that caused it. Zod's `flatten()` keys by the
 *    FIRST path segment only, so a shape refusal names the top-level payload property
 *    (`options`, `screenType`); a rule refusal uses a dotted address into the thing
 *    being edited (see `parseFlowIssuePath` in ./check-in-flow.js).
 *
 * PASSTHROUGH AND UNBOUNDED, which every outbound schema in this package is at least the
 * second half of: this is read off the wire, so a newer server adding a third key must not
 * make a client unable to show the two it does understand. Passthrough specifically —
 * rather than a plain `z.object` — because the key space here belongs to a future server
 * rather than to a row of ours, which is the same distinction `check-in-flow.ts` draws in
 * its header. The values are machine codes and field names, never a submitted value, so
 * nothing here needs redacting before it is logged.
 */
export const validationFailureDetailsSchema = z
    .object({
    formErrors: z.array(z.string()),
    fieldErrors: z.record(z.string(), z.array(z.string())),
})
    .passthrough();
/**
 * `ErrorEnvelope.details` -> the typed body, or `null` when it is not one.
 *
 * `null` covers every legitimate case a client meets: a status other than 400 (where
 * `details` is absent by design), and a 400 from an endpoint whose refusal predates this
 * shape. A caller that gets `null` should fall back to rendering `error` alone — it must
 * not treat the absence as "the save succeeded".
 */
export function parseValidationFailureDetails(details) {
    const parsed = validationFailureDetailsSchema.safeParse(details);
    return parsed.success ? parsed.data : null;
}
export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 20;
/** The shared `?page=&pageSize=&sort=` query contract for every list endpoint. */
export const pageQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    sort: z.string().min(1).max(64).optional(),
});
export function toPage(query) {
    return { skip: (query.page - 1) * query.pageSize, take: query.pageSize };
}
/** Unauthenticated liveness payload served at `GET /api/health`. */
export const healthSchema = z.object({
    status: z.literal('ok'),
    version: z.string(),
    db: z.enum(['up', 'down']),
});
//# sourceMappingURL=http.js.map